import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import values from 'ramda/src/values';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import Filter from '@components/@core/Filter';
import Pagination from '@components/@core/Pagination';
import ManageActions from '~/actions/manage';
import { ReportMenu } from '~/services/config';
import { inPage } from '~/services/util';
import { exportCsv } from '~/services/util-excel';
import AppActions from '~/actions/app';
import EmployeeList from './EmployeeList';
import { TrainingTrackPdf } from './TrackPdf';

const moment = extendMoment(originalMoment);

const TrainingTrack = () => {
    const dispatch = useDispatch();
    const cId = useSelector((state) => state.app.selectedCompany);
    const companies = useSelector((state) => state.app.companies);
    const trainingTrackReport = useSelector((state) => state.manage.trainingTrackReport);
    const toggleModal = (e) => dispatch(AppActions.modalRequest(e));
    const [companyId, setCompanyId] = useState(cId);
    const [dateStart, setDateStart] = useState(moment().startOf('month').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(moment().endOf('month').format('YYYY-MM-DD'));
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);

    const handleDate = (e) => {
        const ds = moment(e.start).format('YYYY-MM-DD');
        const de = moment(e.end).format('YYYY-MM-DD');
        const payload = { company_id: companyId, date_start: ds, date_end: de };
        dispatch(ManageActions.posttrainingtrackreportRequest(payload));
        setDateStart(ds);
        setDateEnd(de);
    };

    const handleFilter = (type, e) => {
        if (type === 'company' && e.length > 0) {
            const cIds = (e[0].id < 0 ? companies : e).map((item) => item.id);
            const payload = { company_id: cIds, date_start: dateStart, date_end: dateEnd };
            dispatch(ManageActions.posttrainingtrackreportRequest(payload));
            setCompanyId(cIds);
        }
    };

    const handleDetail = (item) => () => {
        const { id, users } = item;
        const track = values(users)[0]?.track;
        const payload = { users: values(users), track };
        const route = `/hcm/report-training-track/${Number(id)}`;
        dispatch(ManageActions.trainingtrackdetailreportRequest(payload, route));
    };

    useEffect(() => {
        const payload = { company_id: companyId, date_start: dateStart, date_end: dateEnd };
        dispatch(ManageActions.posttrainingtrackreportRequest(payload));
    }, []);

    const { tracks } = trainingTrackReport;
    const date = moment.range(dateStart, dateEnd);

    const handleExcelReport = () => {
        toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    handleExcel();
                },
            },
        });
    };

    const handleExcel = () => {
        let data = [];
        let key = [];
        tracks.forEach((item) => {
            const track = values(item.users)[0]?.track;
            const assignedBy = `${track?.assigned_by?.profile?.first_name || ''} ${track?.assigned_by?.profile?.last_name || ''}`;
            const ds = moment(track?.created_at).format('MMM DD');
            const de = moment(track?.due_date).format('MMM DD');
            const TrackData = {
                Tracks: item.title,
                AssignedBy: assignedBy,
                Started: ds,
                DueDate: de,
                Assigned: values(item.users).length,
            };
            data.push(TrackData);
        });
        key = ['Tracks', 'Assigned by', 'Started', 'Due Data', 'Assigned'];
        exportCsv(data, key, `Training Track Report`, true);
    };

    const handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                handleExcelReport();
                break;
            case 'print':
                TrainingTrackPdf(tracks);
                break;
        }
    };

    return (
        <ErrorBoundary className="mng-company-track">
            <div className="page-filter">
                <Filter onChange={handleFilter} />
            </div>
            <div className="card">
                <div className="d-h-between mb-3">
                    <span className="dsl-b22 bold">Learning Track Report</span>
                    <div className="d-flex">
                        <DatePicker
                            calendar="range"
                            append="caret"
                            format="MMM DD"
                            align="right"
                            as="span"
                            value={date}
                            disabledDate={(e) => moment(e).isAfter(moment().startOf('month'))}
                            onSelect={handleDate}
                        />
                        <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={handleReportMenu} />
                    </div>
                </div>
                <div className="d-flex py-2 border-bottom">
                    <div className="d-flex-4 d-flex-sm-3 dsl-m12">Tracks</div>
                    <div className="d-flex-2 dsl-m12">Assigned by</div>
                    <div className="d-flex-1 d-flex-sm-2 dsl-m12">Started</div>
                    <div className="d-flex-1 d-flex-sm-2 dsl-m12">Due date</div>
                    <div className="d-flex-1 d-flex-sm-2 dsl-m12 text-end">Assigned</div>
                </div>
                {tracks?.length !== 0 ? (
                    tracks?.map((item, index) => {
                        const track = values(item.users)[0]?.track;
                        const assignedBy = `${track?.assigned_by?.profile?.first_name || ''} ${track?.assigned_by?.profile?.last_name || ''}`;
                        const ds = moment(track?.created_at).format('MMM DD');
                        const de = moment(track?.due_date).format('MMM DD');
                        if (inPage(index, page, perPage)) {
                            return (
                                <div key={item?.id} className="d-flex py-3 cursor-pointer border-bottom" onClick={handleDetail(item)}>
                                    <div className="d-flex-4 d-flex-sm-3 dsl-b14 text-400">{item?.title}</div>
                                    <div className="d-flex-2 dsl-b14 text-400">{assignedBy}</div>
                                    <div className="d-flex-1 d-flex-sm-2 dsl-b14 text-400">{ds}</div>
                                    <div className="d-flex-1 d-flex-sm-2 dsl-b14 text-400">{de}</div>
                                    <div className="d-flex-1 d-flex-sm-2 employees-detail text-end">
                                        <p className="dsl-b14 text-400 mb-0">{values(item.users).length}</p>
                                        <div className="employees-detail-modal">
                                            <EmployeeList users={values(item.users)} />
                                        </div>
                                    </div>
                                </div>
                            );
                        }
                    })
                ) : (
                    <p className="dsl-b-14 mt-3 text-center">No Training Tracks</p>
                )}
                <Pagination current={page} total={Math.ceil(tracks?.length / perPage)} pers={[]} onChange={(e) => setPage(e)} />
            </div>
        </ErrorBoundary>
    );
};

export default TrainingTrack;
